import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import axios from 'axios';
import NavbarAuth from 'components/navBar';
import Button from 'components/button';

const STATUS = {
    LOADING: 'loading',
    VALID: 'valid',
    EXPIRED: 'expired',
    USED: 'used',
    NOT_FOUND: 'not_found',
    ERROR: 'error',
};

const Invite = () => {
    const {token} = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState(STATUS.LOADING);
    const [invitationData, setInvitationData] = useState(null);

    useEffect(() => {
        if (!token) {
            setStatus(STATUS.NOT_FOUND);
            return;
        }

        axios.get(`${process.env.REACT_APP_API_URL}invitation/verify/${token}`)
            .then(res => {
                setInvitationData(res.data);
                setStatus(STATUS.VALID);
            })
            .catch(err => {
                const code = err.response?.status;
                if (code === 410) setStatus(STATUS.EXPIRED);
                else if (code === 400) setStatus(STATUS.USED);
                else if (code === 404) setStatus(STATUS.NOT_FOUND);
                else setStatus(STATUS.ERROR);
            });
    }, [token]);

    const handleRegister = () => {
        navigate(`/register/interimaire?invite=${token}`);
    };

    const handleLogin = () => {
        navigate('/login/interimaire');
    };

    if (status === STATUS.LOADING) {
        return (
            <div className="flex h-screen overflow-hidden">
                <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
                    <NavbarAuth />
                    <main className="h-screen flex items-center justify-center">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                            <p className="text-gray-500">Vérification de l'invitation...</p>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    if (status === STATUS.VALID) {
        const greeting = invitationData?.firstname
            ? `Bonjour ${invitationData.firstname},`
            : 'Bonjour,';

        return (
            <div className="flex h-screen overflow-hidden">
                <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
                    <NavbarAuth />
                    <main className="h-screen flex items-center justify-center">
                        <div className="flex items-center justify-center py-12 px-5 mm:px-6 lg:px-8">
                            <div className="max-w-md w-full space-y-8">
                                <div className="text-center">
                                    <div className="mx-auto mb-6 w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center">
                                        <span className="text-3xl">🎉</span>
                                    </div>
                                    <h2 className="text-3xl font-medium text-[#111827]">
                                        {greeting}
                                    </h2>
                                    <p className="mt-3 text-gray-500">
                                        Vous avez été invité(e) à rejoindre Jobbiz suite à un JobDating.
                                    </p>
                                </div>

                                <div className="rounded-lg border p-6 bg-white space-y-4">
                                    <p className="text-sm text-gray-700 text-center">
                                        Créez votre compte pour accéder à des missions d'intérim et commencer à travailler rapidement.
                                    </p>
                                    {invitationData?.email && (
                                        <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2">
                                            <span className="text-xs text-gray-500">Email :</span>
                                            <span className="text-sm text-gray-900 font-medium">{invitationData.email}</span>
                                        </div>
                                    )}
                                    <Button size={'FULL'} onClick={handleRegister}>
                                        Créer mon compte
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    const errorConfig = {
        [STATUS.EXPIRED]: {
            title: 'Invitation expirée',
            message: 'Cette invitation a expiré. Contactez votre recruteur pour en recevoir une nouvelle.',
            icon: '⏰',
        },
        [STATUS.USED]: {
            title: 'Invitation déjà utilisée',
            message: 'Cette invitation a déjà été utilisée. Si vous avez déjà un compte, connectez-vous.',
            icon: '✅',
        },
        [STATUS.NOT_FOUND]: {
            title: 'Invitation introuvable',
            message: "Ce lien d'invitation n'est pas valide. Vérifiez que vous avez copié le lien complet.",
            icon: '🔍',
        },
        [STATUS.ERROR]: {
            title: 'Erreur',
            message: "Une erreur est survenue lors de la vérification de l'invitation. Réessayez plus tard.",
            icon: '⚠️',
        },
    };

    const config = errorConfig[status] || errorConfig[STATUS.ERROR];

    return (
        <div className="flex h-screen overflow-hidden">
            <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
                <NavbarAuth />
                <main className="h-screen flex items-center justify-center">
                    <div className="flex items-center justify-center py-12 px-5 mm:px-6 lg:px-8">
                        <div className="max-w-md w-full space-y-8 text-center">
                            <div>
                                <div className="mx-auto mb-6 w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center">
                                    <span className="text-3xl">{config.icon}</span>
                                </div>
                                <h2 className="text-3xl font-medium text-[#111827]">
                                    {config.title}
                                </h2>
                                <p className="mt-3 text-gray-500">
                                    {config.message}
                                </p>
                            </div>
                            <div className="flex flex-col gap-3">
                                <Button size={'FULL'} onClick={handleLogin}>
                                    Se connecter
                                </Button>
                                <Button size={'FULL'} color={'SECONDARY'} onClick={() => navigate('/register/interimaire')}>
                                    S'inscrire
                                </Button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Invite;
